import React from "react";
import { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { InvoiceTodoSelectionFormViewProps, InvoiceTodoView } from "models/propTypes/invoicePropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { InvoiceFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DateInputProps, FileUploaderProps, TextInputProps } from "models/propTypes/wintFormPropsTypes";
import { DimensionEnums } from "models/enums";
//#endregion
//#region components
import DimensionsSummary from "./_fields/dimensionsSummary";
import { useCustomerService, useInvoiceService } from "serviceHooks";
import {
  DateInput,
  ExpandableSection,
  ExpandableSectionSummary,
  FileUploader,
  GroupSection,
  InlineLabel,
  TextAreaInput,
  Toggle,
  VerticalFormField,
  WintForm,
  useWintExpandableStep,
  useWintForm,
} from "components/common/wintForms";
import InvoicePreviewSendOptionSection from "./sendMailOptions/previewSendOptionSection";
import ForwardedCostWarnings from "./sendMailOptions/forwardedCostWarnings";
import PeriodisationSummary from "./_fields/periodisationSummary";
import { Grid } from "@mui/material";
import { useDimensionMaps } from "components/common";
import useGetMailOptionDefaults from "./sendMailOptions/useGetMailOptionDefaults";
import useLoadSendMailOptions from "./sendMailOptions/useSendMailOptions";
import useGetPreviewDefaults from "./preview/usePreviewDefaults";
//#endregion
import { AccrualHelper, DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const InvoiceTodoSelectionForm: ForwardRefRenderFunction<FormHandles, InvoiceTodoSelectionFormViewProps> = (
  props,
  ref
) => {
  const invoiceService = useInvoiceService();
  const customerService = useCustomerService();

  const {
    invoice,
    view,
    defaultValues,
    validationSchema,
    handleSubmit,
    onFormValidationChange,
    invoiceDateChangeHandler,
    onInvoiceSendMethodSelected,
    onInvoiceInitialValidationChange,
    onFormUpdate,
  } = props;

  const { wintFormProps, connect, values, formState, setValues } = useWintForm<InvoiceFormTypes.Todo>({
    formRef: ref,
    defaultValues,
    validationSchema,
    handleSubmit,
    onFormValidationChange,
    onFormUpdate,
  });

  const [versionInfo, setVersionInfo] = React.useState<WintSuperkollAPIModel.InvoiceVersionInfo[]>();
  const [sendWarnings, setSendWarnings] = React.useState(invoice.warnings);
  const { forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.Invoice);

  React.useEffect(() => {
    if (!formState.dirty) {
      onInvoiceInitialValidationChange(formState.isValid);
    }
  }, [formState.isValid]);

  React.useEffect(() => {
    invoiceService.getVersionInfo(props.invoice.id).then((res) => setVersionInfo(res));
  }, [invoice.id]);

  const { loadSendMailOptions } = useLoadSendMailOptions();
  const { getCreateInvoiceDefaultEmailOptions } = useGetMailOptionDefaults();
  const { getInitialSendType } = useGetPreviewDefaults();

  React.useEffect(() => {
    Promise.all([
      customerService.getCustomer(invoice.customerId),
      loadSendMailOptions(invoice.id, invoice.customerId),
    ]).then(([customerResult, sendMailOptions]) => {
      if (customerResult) {
        if (invoice.customerId === values.customer?.id) {
          const referencePersonId = invoice?.customerReference?.referenceId;
          let referenceEmail: string | undefined;

          if (referencePersonId !== undefined) {
            referenceEmail = customerResult.referencePersonsList?.find(
              (refPerson) => refPerson.id === referencePersonId
            )?.emailAddress;
          } else {
            referenceEmail = customerResult.referencePersonsList?.find(
              (refPerson) => refPerson.isDefault
            )?.emailAddress;
          }

          const mailOptions: InvoiceFormTypes.SendMailOptions = {
            ...sendMailOptions,
            defaultEmailTo: {
              referencePerson: referenceEmail,
              customer: customerResult.emailAddress,
            },
          };

          const defaultEmailToSettings: InvoiceFormTypes.DefaultEmailToSettings = {
            emailToType: customerResult.invoiceDeliverySettings?.invoiceMailToType,
            customEmail: customerResult.invoiceDeliverySettings?.customInvoiceMailTo,
          };

          setValues((prevState) => ({
            ...prevState,
            sendType:
              prevState.sendType === defaultValues.sendType
                ? getInitialSendType(
                    {
                      creditedInvoiceId: invoice.creditedInvoiceId,
                      customerDefaultSendType: customerResult.invoiceDeliverySettings?.defaultInvoiceSendOption,
                    },
                    invoice
                  )
                : prevState.sendType,
            mailOptions: mailOptions,
            sendMail: getCreateInvoiceDefaultEmailOptions(mailOptions, defaultEmailToSettings, invoice.invoiceSendInfo),
            loadingMailOptions: false,
            defaultEmailToSettings: defaultEmailToSettings,
          }));
        }
      }
    });
  }, [invoice.id]);

  const invoiceDateProps = connect("otherSettings.invoiceDate");
  const useTodayProps = connect("useToday");

  const setInvoiceDateValue = (newValue: Date, shouldValidate?: boolean) => {
    invoiceDateProps.setValue(newValue, shouldValidate);
    connect("otherSettings.dueDate").setValue(DateHelper.addDays(newValue, invoice.paymentTerms), shouldValidate);
    invoiceDateChangeHandler && invoiceDateChangeHandler(newValue);
  };

  const setUseTodayValue = (newValue: boolean, shouldValidate?: boolean) => {
    useTodayProps.setValue(newValue, shouldValidate);
    const newDate = newValue ? new Date() : invoice.postingDate;
    setInvoiceDateValue(newDate, shouldValidate);
  };

  const invoicePut = React.useMemo(() => invoiceService.convertTodoFormToAPIRequest(values, invoice), [values]);

  const expandableStepProps = useWintExpandableStep(formState);

  const periodisationSummary =
    invoice.rows?.map((row) => {
      const r = row as typeof row & { periodisationStartDate?: string; periodisationEndDate?: string };
      const hasDateFields = r.periodisationStartDate !== undefined && r.periodisationStartDate !== null;
      const hasMonthFields =
        (row.periodisationStartYear !== undefined || row.periodisationStartYear !== null) &&
        (row.periodisationStartMonth !== undefined || row.periodisationStartMonth !== null) &&
        (row.periodisationEndYear !== undefined || row.periodisationEndYear !== null) &&
        (row.periodisationEndMonth !== undefined || row.periodisationEndMonth !== null);
      const hasAnyData = hasDateFields || hasMonthFields;
      return {
        text: row.overriddenDescription ?? row.description,
        mode: (hasDateFields ? "date" : "month") as "date" | "month",
        periodisation:
          row.periodisation && hasAnyData
            ? hasDateFields
              ? {
                  start: r.periodisationStartDate ? DateHelper.parseDateOnly(r.periodisationStartDate) : undefined,
                  end: r.periodisationEndDate ? DateHelper.parseDateOnly(r.periodisationEndDate) : undefined,
                }
              : {
                  start: DateHelper.getDateFromYearMonth(
                    (row.periodisationStartYear ?? 0) * 100 + (row.periodisationStartMonth ?? 0)
                  ),
                  end: DateHelper.getDateFromYearMonth(
                    (row.periodisationEndYear ?? 0) * 100 + (row.periodisationEndMonth ?? 0)
                  ),
                }
            : undefined,
      };
    }) ?? [];
  const dimensionsSummary = invoiceService.getRowDimensionData(invoiceService.getProducts(invoice));

  return view === undefined || view === InvoiceTodoView.None ? (
    <></>
  ) : (
    <WintForm {...wintFormProps}>
      <Grid container className={cssExports.sectionedForm} marginBottom={-2.5}>
        <div className={view === InvoiceTodoView.BaseForm ? undefined : cssExports.hidden}>
          <ExpandableSection
            key="baseFields"
            summary={
              <ExpandableSectionSummary
                title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.UpdateDate)}
                subText={
                  values.useToday
                    ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Optional)
                    : undefined
                }
              />
            }
            initiallyOpen
            {...expandableStepProps}
            hasErrors={invoiceDateProps.hasError}
          >
            {!values.useToday && (
              <VerticalFormField<DateInputProps>
                Component={DateInput}
                {...invoiceDateProps}
                setValue={setInvoiceDateValue}
                label={TranslationKeys.Invoice.InvoiceDate}
              />
            )}
            <Grid container direction="row" wrap="nowrap" columnGap={1}>
              <InlineLabel columns>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.UseToday)}
              </InlineLabel>

              <Grid item>
                <Toggle {...useTodayProps} setValue={setUseTodayValue} />
              </Grid>
            </Grid>
          </ExpandableSection>

          {dimensionsSummary?.length ? (
            <ExpandableSection
              key="dimensions"
              initiallyOpen={!!dimensionsSummary?.length}
              summary={
                <ExpandableSectionSummary
                  title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Tags)}
                  subText={
                    forcedDimensionMap.length === 0
                      ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Optional)
                      : undefined
                  }
                />
              }
              {...expandableStepProps}
            >
              <DimensionsSummary
                value={dimensionsSummary}
                amount={invoice.totalAmount}
                currency={invoice.currency ?? ""}
              />
            </ExpandableSection>
          ) : (
            <></>
          )}

          {values.products?.some((p) => p.periodisation?.range !== null) &&
          periodisationSummary?.some((p) => p.periodisation !== undefined) ? (
            <ExpandableSection
              key="periodisation"
              summary={
                <ExpandableSectionSummary
                  title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accrual)}
                  subText={AccrualHelper.getRowAccuralSummary(periodisationSummary)}
                />
              }
              initiallyOpen={AccrualHelper.getIsSeveralMonths(periodisationSummary)}
              {...expandableStepProps}
            >
              <PeriodisationSummary value={periodisationSummary} />
            </ExpandableSection>
          ) : (
            <></>
          )}
        </div>

        <div className={view === InvoiceTodoView.BaseForm ? cssExports.hidden : undefined}>
          <ExpandableSection
            key="messageAndAttachments"
            summary={
              <ExpandableSectionSummary
                title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.MessagesAndAttachments)}
                subText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Optional)}
              />
            }
            initiallyOpen={false}
            {...expandableStepProps}
          >
            <VerticalFormField<TextInputProps>
              Component={TextAreaInput}
              {...connect("notes")}
              label={TranslationKeys.Common.Message}
              descriptionText={TranslationKeys.Invoice.MessageDescription}
              optional
            />
            <VerticalFormField<FileUploaderProps>
              Component={FileUploader}
              {...connect("attachments")}
              label={TranslationKeys.Common.Attachments}
              descriptionText={TranslationKeys.Invoice.AttachmentsDescription}
              singleSelection={false}
            />
          </ExpandableSection>

          <ForwardedCostWarnings warnings={sendWarnings} />
          <GroupSection
            header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendAs)}
            className={clsx(cssExports.noBorder, cssExports.asExpandable)}
          >
            <InvoicePreviewSendOptionSection
              invoiceId={invoice.id}
              connect={connect}
              values={values}
              setValues={setValues}
              version={versionInfo?.length ? versionInfo[versionInfo.length - 1].version : undefined}
              invoicePut={invoicePut}
              isReminder={false}
              status={invoice.status}
              canSendLastVersionToPagero={invoice?.canSendLastVersionToPagero}
              warnings={invoice?.warnings}
              noGrouping
              onWarningsChange={setSendWarnings}
              onInvoiceSendMethodSelected={onInvoiceSendMethodSelected}
              nextInvoiceSerialNumber={props.nextInvoiceSerialNumber}
              // The effect above already loads the customer. A second load races it, and Formik's functional
              // setValues reads the last render's values, so it could write the company default back over sendType.
              skipSendInformationLoad
            />

            {values.sendType === WintSuperkollAPIModel.InvoiceSendMethod.MarkAsSent && (
              <Grid item className={clsx(cssExports.formControlDescription, cssExports.verticalFormDescription)}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.MarkAsSentDescription)}
              </Grid>
            )}
          </GroupSection>
        </div>
      </Grid>
    </WintForm>
  );
};

export default forwardRef(InvoiceTodoSelectionForm);
